import { useQuery, useMutation } from "@apollo/client/react";
import {
  GetCommunicationssDocument,
  GetCommunicationsDocument,
  UpdateCommunicationsDocument,
  DeleteCommunicationsDocument,
  GetCommunicationssQuery,
  GetCommunicationsQuery,
  CommunicationsUpdateInput,
  UpdateCommunicationsMutation,
  DeleteCommunicationsMutation,
  Communications,
} from "@/src/gql/graphql";
import { getEntityTableConfig } from "@/lib/entity-registry";

// Lazily resolved (NOT at module top-level): calling getEntityTableConfig during
// this module's evaluation forms a require cycle with `@/lib/entity-registry`
// under CommonJS/jest, leaving it `undefined`. Deferring to first hook call runs
// it after all modules initialize. See TESTING_SYSTEMS.
let _communicationsConfig: ReturnType<typeof getEntityTableConfig<Communications>> | undefined;
const communicationsConfig = (): ReturnType<typeof getEntityTableConfig<Communications>> =>
  (_communicationsConfig ??= getEntityTableConfig<Communications>("communications"));
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import { useCommunicationsPaginated } from "./communications-paginated";
import { useLogCommunication } from "./communications-mutations";
import { usePaginatedCollection } from "@/lib/hooks/api/shared/use-paginated-collection";

/**
 * Module-level selector/mapper. `usePaginatedCollection` lists them as memo
 * dependencies, so an inline arrow here would allocate a fresh function every
 * render and degrade the list memo to allocate-per-render (MEMO_STABLE_PROPS).
 * Both are pure, so hoisting can never go stale.
 */
type CommunicationsQueryNode = NonNullable<
  GetCommunicationssQuery["communicationsCollection"]
>["edges"][number]["node"];

const selectCommunicationsConnection = (data: GetCommunicationssQuery) =>
  data.communicationsCollection;
const mapCommunicationNode = (node: CommunicationsQueryNode): Communications =>
  node as Communications;

type CommunicationsEntityHooks = EntityHooks<Communications> & {
  queryDocument: typeof GetCommunicationssDocument;
  config: ReturnType<typeof communicationsConfig>;
};

/**
 * Allocated ONCE, on first call. The object is a `useMemo` dependency in
 * `app/communications/page.tsx`, `app/crm/communications/page.tsx` and
 * `features/deals/components/DealCommunications.tsx`, so a fresh literal per
 * call made those memos dead (MEMO_STABLE_PROPS). None of the members closes
 * over anything render-scoped, and the factory calls no hook of its own, so a
 * singleton is correct under every call pattern.
 *
 * It is built LAZILY rather than at module scope because `config` resolves
 * `getEntityTableConfig`, which forms a require cycle during this module's
 * evaluation (see the note on `communicationsConfig` above).
 */
let _communicationsEntityHooks: CommunicationsEntityHooks | undefined;

/**
 * Communications hooks using generated GraphQL operations
 * Implements EntityHooks interface for use with UniversalEntityPage and UniversalDetailPage
 */
export const useCommunicationsHooks = (): CommunicationsEntityHooks =>
  (_communicationsEntityHooks ??= {
    queryDocument: GetCommunicationssDocument,

    usePaginated: (params?: Record<string, unknown>) =>
      // Canonical list-query path (cache-and-network) — see use-paginated-collection.ts.
      usePaginatedCollection(
        GetCommunicationssDocument,
        params,
        selectCommunicationsConnection,
        mapCommunicationNode
      ),

    useInfinite: (_params?: Record<string, unknown>) => {
      return {
        data: undefined,
        isLoading: false,
        error: null,
        refetch: () => {},
        hasNextPage: false,
        isFetchingNextPage: false,
        fetchNextPage: () => {},
      };
    },

    useSingle: (id: string, options?: { enabled?: boolean }) => {
      const result = useQuery<GetCommunicationsQuery>(GetCommunicationsDocument, {
        variables: { id },
        errorPolicy: "all",
        skip: !id || (options && options.enabled === false),
      });
      const node = result.data?.communicationsCollection?.edges?.[0]?.node;
      return {
        data: node ? (node as Communications) : undefined,
        isLoading: result.loading,
        error: result.error ? new Error(result.error.message || String(result.error)) : null,
        refetch: result.refetch,
      };
    },

    useCreate: () => useLogCommunication(),

    useUpdate: () => {
      const [mutate, result] = useMutation<UpdateCommunicationsMutation>(
        UpdateCommunicationsDocument
      );
      return {
        mutateAsync: async ({ id, ...input }: CommunicationsUpdateInput & { id: string }) => {
          const { __typename: _typename, ...cleanInput } = input as CommunicationsUpdateInput & {
            __typename?: unknown;
          };

          const { data, error } = await mutate({
            variables: { id, input: cleanInput },
            errorPolicy: "all",
            refetchQueries: ["GetCommunicationss", "GetCommunications"],
            awaitRefetchQueries: true,
          });

          if (error) throw new Error(`GraphQL error: ${error.message}`);
          const updatedCommunication = data?.updatecommunicationsCollection?.records?.[0];
          if (!updatedCommunication) throw new Error("Failed to update communication");
          return updatedCommunication as Communications;
        },
        isPending: result.loading,
      };
    },

    useDelete: () => {
      const [mutate, result] = useMutation<DeleteCommunicationsMutation>(
        DeleteCommunicationsDocument
      );
      return {
        mutateAsync: async (id: string): Promise<string> => {
          const { data, error } = await mutate({
            variables: { id },
            errorPolicy: "all",
            refetchQueries: ["GetCommunicationss"],
            awaitRefetchQueries: true,
          });

          if (error) throw new Error(`GraphQL error: ${error.message}`);
          const deletedRecord = data?.deleteFromcommunicationsCollection?.records?.[0];
          return (deletedRecord?.id || id);
        },
        isPending: result.loading,
      };
    },

    config: communicationsConfig(),
  });

export { useCommunicationsPaginated };
